import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { useLucideIcon } from '../../builder/primitives/lucideRegistry'
import { DEFAULT_PICTO, isPictoName } from './picto'

/**
 * Le picto d'une entité (projet, espace wiki) : une icône SVG Lucide désignée
 * par son NOM, celui que pose la bibliothèque d'icônes (`PictoPickerField`).
 *
 * Pourquoi ce composant plutôt que la primitive `LucideIcon` : la donnée déjà
 * saisie porte encore des EMOJI (le picto était un caractère avant d'être une
 * icône). `LucideIcon` rend une icône de repli pour toute valeur qu'elle ne
 * résout pas — un projet créé avant la bascule verrait son picto remplacé par
 * un « ? ». Ici, une valeur qui n'est PAS un nom Lucide est rendue telle
 * quelle : l'emoji reste lisible jusqu'à ce qu'on choisisse une icône. Aucune
 * condition YAML ne sait faire cette distinction (ni regex, ni test de casse).
 *
 * La bascule sert même une fois la migration jouée
 * (`orbit_front_app_update_11002`) : une instance dont le front est déployé
 * avant `updb`, et le champ `picto` d'un groupe autre que `project` (encore
 * saisissable en texte libre depuis le back-office), y retombent.
 */
export const Picto = ({ name, size = 20, fallback = DEFAULT_PICTO, ...rest }) => {
  const target = isPictoName(name) ? name : null
  const Icon = useLucideIcon(target)
  const FallbackIcon = useLucideIcon(fallback)

  // `rest` porte le `className` et les autres attributs déclarés par l'appelant
  // (YAML compris) : il doit être répandu sur CHAQUE branche, sinon un
  // `attributes: { class: … }` disparaît selon l'état du chargement.
  if (target) {
    // Une icône hors registre arrive par import dynamique : réserver la place
    // le temps du chargement évite le saut de mise en page.
    if (Icon === undefined) {
      const dim = typeof size === 'number' ? `${size}px` : size
      return <span aria-hidden="true" {...rest} style={{ display: 'inline-block', width: dim, height: dim }} />
    }
    const Resolved = Icon || FallbackIcon
    return Resolved ? <Resolved size={size} aria-hidden="true" {...rest} /> : null
  }

  // Valeur héritée (emoji) : telle quelle — un repli l'effacerait.
  if (name) return <span aria-hidden="true" {...rest}>{name}</span>

  return FallbackIcon ? <FallbackIcon size={size} aria-hidden="true" {...rest} /> : null
}

/**
 * Élément reactive-json — `- { type: PictoIcon, name: ~.icon, size: 24 }`.
 */
export const PictoIcon = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })
  const attributes = useEvaluatedAttributes(props.attributes ?? {})

  return (
    <ActionDependant {...props}>
      <Picto
        name={resolve(props.name)}
        size={resolve(props.size) ?? 20}
        fallback={resolve(props.fallback) ?? DEFAULT_PICTO}
        {...attributes}
      />
    </ActionDependant>
  )
}
